import { TFile } from "../models/file-model";
import { useParams } from "react-router-dom";
import { ComponentProps, useEffect, useState } from "react";
import { getFilesRetriever } from "../services/files-retriever";
import { Severity } from "../components/Message/MessageContainer";
import { childHandshake, sendEventToParent } from "../services/dialog-service";
import { AttachFilesContainer } from "../components/AttachFiles/AttachFilesContainer";
import { LoadingPresentational } from "../components/Loading/LoadingPresentational";
import { NotificationsContainer } from "../components/Notifications/NotificationsContainer";
import { getFolderService } from "../services/draft-attachments-service";
import { MetricPayload, UNKNOWN_METRICS_VALUE } from "../models/metric-model";
import { createAuthorisedApiRequest } from "../helpers/create-authorised-request";
import { fetchWrapper } from "../helpers/fetch-wrapper";
import { getDiagnostics } from "../helpers/DiagnosticsHelper";
import { logAttachFilesUsageMetric } from "../helpers/MetricsHelper";
import { AttachFilesMetricActionConsts } from "../helpers/MetricsHelper";

export const AttachFilesPage = () => {
  const [ready, setReady] = useState(false);

  const folderService = getFolderService();
  const filesRetriever = getFilesRetriever();
  const params: { matterId: string } = useParams();

  useEffect(() => {
    childHandshake(() => {
      setReady(true);
    });
  }, []);

  const onAttach = async (selected?: TFile[]): Promise<void> => {
    const metricName = "outlook_addin_attachment_added";

    try {
      const diagnostics = getDiagnostics();
      
      const metricPayload: MetricPayload = {
        Metric: {
          Name: metricName,
          Timestamp: new Date().toISOString(), // Current UTC time
          Dimensions: {
              Platform: diagnostics?.platform || UNKNOWN_METRICS_VALUE,
              Version: diagnostics?.version || UNKNOWN_METRICS_VALUE,
              HostName: diagnostics?.hostName || UNKNOWN_METRICS_VALUE,
              HostVersion: diagnostics?.hostVersion || UNKNOWN_METRICS_VALUE,
              Product: ""
          }
        }
      };
      
      const { url, requestInit } = await createAuthorisedApiRequest("AddMetricUrl", metricPayload);
      const result = await fetchWrapper(url, requestInit);  

      if (result.err) {
          console.error(`Add metric call returned an error when saving ${metricName} metric: `, result.err);
      }
    } catch (error) {
      console.error(`Error occurred when saving ${metricName} metric: `, error);
    }

    sendEventToParent({ type: "ATTACH", files: selected || [] });
  };

  const onCancel = async () => {
    // Log the metrics before cancelling the dialog window otherwise the context has changed during the async call and 
    // the metric logging will never get called.
    await logAttachFilesUsageMetric(AttachFilesMetricActionConsts.cancel);
    
    sendEventToParent({ type: "CANCEL" });    
  };
    
  return (
    <AttachFilesPageInjectable
      {...{ onCancel, onAttach, folderService, filesRetriever, ready, matterId: params.matterId }}
    />
  );
};

export const AttachFilesPageInjectable = (
  props: ComponentProps<typeof AttachFilesContainer> & { matterId: string; ready: boolean }
) => (
  <div className="flex flex-col h-screen w-screen p-6">
    <NotificationsContainer showTopN={1} />
    <div className="flex-grow overflow-auto">
      {props.ready ? (
        <AttachFilesContainer
          {...{
            matterId: props.matterId,
            onCancel: props.onCancel,
            onAttach: props.onAttach,
            folderService: props.folderService,
            filesRetriever: props.filesRetriever,
          }}
        />
      ) : (
        <LoadingPresentational
          isLoading={true}
          title="Loading files"
          severity={Severity.Information}
          message="Please wait while we load files to select..."
        />
      )}
    </div>
  </div>
);
